import { MouseEvent, ReactNode } from "react";
import { AlertTriangle, Globe, LucideIcon } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { parseExternalUrl, parseLinkedInUrl, ParsedExternalUrl } from "@/utils/external-url";

export type ExternalLinkKind = "linkedin" | "portfolio";

/** Matches the shapes already used across the app. */
export type ExternalLinkVariant = "icon" | "pill" | "inline" | "button";

export interface ExternalLinkButtonProps {
  kind: ExternalLinkKind;
  url: string | null | undefined;
  variant?: ExternalLinkVariant;
  /** Text next to the icon. Omit for icon-only. */
  label?: ReactNode;
  /** Hide entirely when the field is empty. Invalid values always render. */
  hideWhenEmpty?: boolean;
  /** Fires only when the link is actually navigable, for view tracking. */
  onOpen?: () => void;
  /** Um clique simples abre isto dentro da plataforma (ex.: aba LinkedIn do painel); Cmd ou Ctrl+clique segue para o site. */
  onOpenInPlatform?: () => void;
  icon?: LucideIcon;
  className?: string;
  iconClassName?: string;
  /** false quando um Tooltip por fora já explica o link (evita dois balões). */
  nativeTitle?: boolean;
  /** `linkedin_nao_encontrado_em` ou `portfolio_invalido_em`: a verificação automática não abriu essa URL. */
  notFoundAt?: string | null;
}

export const LINKEDIN_NOT_FOUND_WARNING =
  "LinkedIn não encontrado na última busca automática: perfil removido, privado ou URL errada";

export const PORTFOLIO_INVALID_WARNING =
  "Portfólio não abriu na última verificação automática: site fora do ar, página não encontrada ou endereço errado";

const NOT_FOUND_WARNING: Record<ExternalLinkKind, string> = {
  linkedin: LINKEDIN_NOT_FOUND_WARNING,
  portfolio: PORTFOLIO_INVALID_WARNING,
};

const KIND_LABEL: Record<ExternalLinkKind, string> = {
  linkedin: "LinkedIn",
  portfolio: "Portfolio",
};

const KIND_ICON: Record<ExternalLinkKind, LucideIcon> = {
  linkedin: Linkedin,
  portfolio: Globe,
};

const VARIANT_CLASS: Record<ExternalLinkVariant, string> = {
  icon: "",
  pill: "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
  inline: "inline-flex items-center gap-1 transition-colors",
  button: "inline-flex items-center gap-2",
};

const VARIANT_STATE_CLASS: Record<
  ExternalLinkVariant,
  { ok: string; unavailable: string; invalid: string }
> = {
  icon: {
    ok: "text-slate-600 hover:bg-primary/10 hover:text-primary",
    unavailable: "text-muted-foreground/30",
    invalid: "text-destructive/70",
  },
  pill: {
    ok: "bg-blue-50 text-blue-800 hover:bg-blue-100",
    unavailable: "bg-muted text-muted-foreground/40 cursor-not-allowed",
    invalid: "bg-destructive/10 text-destructive cursor-not-allowed",
  },
  inline: {
    ok: "text-muted-foreground hover:text-primary",
    unavailable: "text-muted-foreground/30 cursor-not-allowed",
    invalid: "text-destructive cursor-not-allowed",
  },
  // Button already styles the disabled state; only "invalid" needs a tint.
  button: {
    ok: "",
    unavailable: "",
    invalid: "border-destructive/40 text-destructive",
  },
};

function truncate(value: string, max = 90): string {
  return value.length > max ? `${value.slice(0, max)}...` : value;
}

function buildTitle(kind: ExternalLinkKind, parsed: ParsedExternalUrl, inPlatform: boolean): string {
  if (parsed.status === "empty") return `${KIND_LABEL[kind]} não cadastrado`;
  if (!parsed.warning) return inPlatform ? `${KIND_LABEL[kind]} no painel (Cmd ou Ctrl+clique abre o site)` : KIND_LABEL[kind];
  return `${parsed.warning} (${truncate(parsed.raw)})`;
}

function isPlainClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

/**
 * Renders an external link (LinkedIn or portfolio) with a single, consistent
 * treatment for the four states the stored value can be in: missing, unusable,
 * usable but suspicious, and fine. An unusable value never becomes an `<a>`,
 * which is what used to produce anchors with an empty href.
 */
export function ExternalLinkButton({
  kind,
  url,
  variant = "icon",
  label,
  hideWhenEmpty = true,
  onOpen,
  onOpenInPlatform,
  icon,
  className,
  iconClassName,
  nativeTitle = true,
  notFoundAt,
}: ExternalLinkButtonProps) {
  const parsedUrl = kind === "linkedin" ? parseLinkedInUrl(url) : parseExternalUrl(url);
  // Perfil não encontrado e portfólio que não abriu continuam clicáveis (pode
  // abrir pra quem está logado, ou o site voltar), só ganham o aviso amarelo.
  const parsed: ParsedExternalUrl =
    notFoundAt && parsedUrl.href && !parsedUrl.warning
      ? { ...parsedUrl, warning: NOT_FOUND_WARNING[kind] }
      : parsedUrl;
  if (parsed.status === "empty" && hideWhenEmpty) return null;

  const Icon = icon ?? KIND_ICON[kind];
  const title = nativeTitle ? buildTitle(kind, parsed, !!onOpenInPlatform) : undefined;
  const stateClass =
    parsed.status === "invalid"
      ? VARIANT_STATE_CLASS[variant].invalid
      : parsed.status === "empty"
        ? VARIANT_STATE_CLASS[variant].unavailable
        : VARIANT_STATE_CLASS[variant].ok;

  const flag = parsed.warning ? (
    <AlertTriangle
      className={cn(
        "shrink-0",
        variant === "icon"
          ? "absolute -right-0.5 -top-0.5 h-2.5 w-2.5"
          : "h-3 w-3",
        parsed.status === "invalid" ? "text-destructive" : "text-amber-500",
      )}
    />
  ) : null;

  const content = (
    <>
      <Icon className={cn(variant === "icon" ? "h-4 w-4" : "h-3.5 w-3.5", iconClassName)} />
      {label}
      {flag}
    </>
  );

  // "icon" and "button" delegate their box to <Button>; the others style the
  // element themselves.
  const isWrapped = variant === "icon" || variant === "button";
  const innerClass = isWrapped
    ? cn(VARIANT_CLASS[variant], "relative")
    : cn(VARIANT_CLASS[variant], stateClass, className);

  // Expostos para o e2e asseverar o estado sem depender de classe de estilo.
  const testProps = {
    "data-testid": `external-link-${kind}`,
    "data-status": parsed.status,
    "data-warning": parsed.warning ?? undefined,
  };

  const inner = parsed.href ? (
    <a
      {...testProps}
      href={parsed.href}
      target="_blank"
      rel="noopener noreferrer"
      title={title}
      onClick={(event) => {
        event.stopPropagation();
        onOpen?.();
        if (onOpenInPlatform && isPlainClick(event)) {
          event.preventDefault();
          onOpenInPlatform();
        }
      }}
      className={innerClass}
    >
      {content}
    </a>
  ) : (
    <span {...testProps} className={innerClass} title={title}>
      {content}
    </span>
  );

  if (!isWrapped) return inner;

  return (
    <Button
      asChild={!!parsed.href}
      disabled={!parsed.href}
      variant={variant === "icon" ? "ghost" : "outline"}
      size={variant === "icon" ? "icon" : "default"}
      title={parsed.href ? undefined : title}
      className={cn(
        variant === "icon" && "h-7 w-7 transition-colors",
        // Keep the destructive tint at full strength instead of Button's dimming.
        parsed.status === "invalid" && "disabled:opacity-100",
        stateClass,
        className,
      )}
    >
      {inner}
    </Button>
  );
}
